import { DOCUMENT } from '@angular/common';
import { ApplicationRef, ComponentFactoryResolver, EmbeddedViewRef, Inject, Injectable, Injector, Type } from '@angular/core';
import { DialogComponent } from '../dialog.component';
import { DialogData } from '../model/dialog-data';
import { DialogIdentifier } from '../model/dialog-identifier';
import { DialogInjector } from '../model/dialog-injector';
import { DialogResponse } from '../model/dialog-response';
import { DialogToggle } from '../model/dialog-toggle';
import { ParentDialogConfiguration } from '../model/parent-dialog-configuration';

@Injectable({
  providedIn: 'root'
})
export class DialogService {

  constructor(
    private parentComponentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private applicationRef: ApplicationRef,
    @Inject(DOCUMENT) private document
  ) { }

  public create<T, R>(componentType: Type<any>, input?: T, configuration?: ParentDialogConfiguration): DialogResponse<R>;
  public create<T, R>(componentType: Type<any>, input?: T, prefixNodeId?: string, configuration?: ParentDialogConfiguration)
    : DialogResponse<R>;

  public create<T, R>(componentType: Type<any>, input?: T, paramA?: string | object, paramB?: object): DialogResponse<R> {
    if (typeof paramA === 'string') {
      return this.createAndInjectParentComponent<T, R>(componentType, input, paramA, paramB);

    } else if (typeof paramA === 'object') {
      return this.createAndInjectParentComponent<T, R>(componentType, input, undefined, paramA);

    } else {
      return this.createAndInjectParentComponent<T, R>(componentType, input, paramA, paramB);
    }
  }

  private createAndInjectParentComponent<T, R>(
    componentType: Type<any>, input?: T, prefixNodeId?: string, configuration?: ParentDialogConfiguration
  ) {

    const ownTokens = this.ownTokens<T, R>(input, prefixNodeId);

    const parentComponentFactory = this.parentComponentFactoryResolver.resolveComponentFactory(DialogComponent);
    const parentComponentRef = parentComponentFactory.create(new DialogInjector(this.injector, ownTokens));

    parentComponentRef.instance.parentDialogConfiguration = configuration;

    const parentHostView = parentComponentRef.hostView;
    const parentNode = (parentHostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

    this.applicationRef.attachView(parentHostView);
    this.document.body.appendChild(parentNode);

    parentComponentRef.instance.sonComponentType = componentType;
    parentComponentRef.instance.parentComponentRef = parentComponentRef;


    const response: DialogResponse<R> = {
      afterClosed$: (ownTokens.get(DialogToggle) as DialogToggle<R>).afterClosed$,
      parentComponent: parentComponentRef.instance,
      destroy: () => {
        this.applicationRef.detachView(parentHostView);
        parentComponentRef.destroy();
        this.document.body.style.overflow = 'initial';
      }
    };

    return response;
  }

  private ownTokens<T, R>(input?: T, prefixNodeId?: string) {
    const weakMap = new WeakMap<any, any>();

    const dialogToggle = new DialogToggle<R>();
    const dialogData = new DialogData<T>();
    const dialogIdentifier = new DialogIdentifier();
    dialogData.data = input;
    dialogIdentifier.prefixNodeId = prefixNodeId;

    weakMap.set(DialogToggle, dialogToggle);
    weakMap.set(DialogData, dialogData);
    weakMap.set(DialogIdentifier, dialogIdentifier);

    console.log('[bcd] ownTokens - weakMap: ', weakMap);

    return weakMap;
  }

}
